import { transition } from '@pixelunion/animations';

import HeaderNavMenu from './HeaderNavMenu'; // eslint-disable-line import/no-cycle

export default class HeaderNavParent {
  constructor(options) {
    this.menuItem = options.menuItem;
    this.detailsItem = options.menuItem.querySelector('[data-header-nav-details]');
    this.parentMenu = options.parentMenu;

    this.menuLink = this.detailsItem.querySelector('[data-header-nav-link]');
    this.submenuEl = this.menuItem.querySelector('[data-header-nav-submenu]');

    this._isOpen = false;
    this.submenu = null;

    this.abortController = new AbortController();
    this.escAbortController = null;

    this._open = this._open.bind(this);
    this._close = this._close.bind(this);
    this.close = this.close.bind(this);

    // When a user hits the escape key all of the menus should close and the keyboard focus should return
    // to the parent link. This will bubble up to the top parent menu.
    this.closeEsc = e => {
      if (e.key === 'Escape') {
        this.menuLink.focus();
        this._close();
      }
    };

    // When the mouse leaves the open menu, we don't want to close it right away. Setting a delay on close
    // allows situations where you might go to another parent menu but your mouse would drag slightly outside
    // of the open menu, causing you to have to go back and open the menu again. This improves accessibility
    // as well as general usability.
    this.closeTimer = null;
    this.mouseover = () => {
      clearTimeout(this.closeTimer);
      this._open();
    };

    this.mouseout = () => {
      this.closeTimer = setTimeout(this.close, 400);
    };

    this.click = e => {
      e.stopPropagation();
      // If the menu is already open, we want to continue to the link in the <a> tag.
      // For mouse hover, this is pretty straightforward, but with keyboard and touch navigation
      // you will have to touch or trigger that link a second time. This is maybe not the best for
      // accessibility, but it's a good compromise for now.

      const clickedParent = e.target.closest('[data-header-nav-link]');

      if (clickedParent && clickedParent.dataset.href) {
        // We only prevent the click through if the menu wasn't already open.
        e.preventDefault();

        if (this._isOpen) {
          window.location = clickedParent.dataset.href;
          return;
        }
      }

      // If the submenu isn't open, open it
      this._open();
    };

    // During keyboard navigation, when you focus onto the parent menuItem, this will close the other
    // menu items that were previously open. This usually happens when you were navigating within
    // a submenu and then tab out of it to the next menu item at a lower tier.
    this.focusin = e => {
      e.stopPropagation();
      this.parentMenu.closeSiblings(this);
    };

    // Set up animation event
    this.animation = transition({ el: this.submenuEl, state: 'closed' });

    const { signal } = this.abortController;
    this.menuItem.addEventListener('mouseover', () => this.mouseover(), { signal });
    this.menuItem.addEventListener('mouseout', () => this.mouseout(), { signal });
    this.menuItem.addEventListener('click', e => this.click(e), { signal });
    this.menuItem.addEventListener('touchend', e => this.click(e), { signal });
    this.menuItem.addEventListener('focusin', e => this.focusin(e), { signal });

    // If the user clicks, taps, or their keyboard focus reaches outside of the menu
    // we should close this menu.
    document.body.addEventListener('click', () => this._close(), { signal });
    document.body.addEventListener('focusin', () => this._close(), { signal });
  }

  forceOpen() {
    return this._open();
  }

  forceClose() {
    return this._close();
  }

  close() {
    this._close();
  }

  _open() {
    if (this._isOpen) return;

    this._isOpen = true;
    this.parentMenu.closeSiblings(this);

    this.escAbortController = new AbortController();
    window.addEventListener('keydown', e => this.closeEsc(e), { signal: this.escAbortController.signal });

    if (!this.submenu) {
      this.submenu = new HeaderNavMenu({ navList: this.submenuEl });
    }

    this.submenuEl.classList.remove('alternate-drop');

    this.animation.animateTo('open', {
      onStart: ({ el }) => {
        // Check to see if dropdown is offscreen and apply a class so it can be styled differently.
        const menuBounds = el.getBoundingClientRect();
        if (menuBounds.right > document.documentElement.clientWidth) {
          el.classList.add('alternate-drop');
        }
        if (menuBounds.left < 0) {
          el.classList.add('alternate-drop-left');
        }
      }
    });

    this.menuLink.setAttribute('aria-expanded', true);
    this.detailsItem.setAttribute('open', 'open');
  }

  _close() {
    if (!this._isOpen) return;

    if (this.submenu) {
      this.submenu.unload();
      this.submenu = null;
    }

    this._isOpen = false;

    if (this.escAbortController) {
      this.escAbortController.abort();
      this.escAbortController = null;
    }

    this.animation.animateTo('closed')
      .then(() => {
        this.submenuEl.classList.remove('alternate-drop');
        this.menuLink.setAttribute('aria-expanded', false);
        this.detailsItem.removeAttribute('open');
      });
  }

  unload() {
    this.forceClose();
    this.abortController.abort();
  }
}
